/**
 * 🦸 Hero Classes V1 — class selection · stats panel · Phoenix rebirth UI
 *
 * - Pre-selection: 4 class cards in a comparison grid → confirm modal → lock-in.
 * - Post-selection: stats dashboard · XP progress · CCLL info · rebirth flow.
 * - Rebirth: "Ask each time" — modal pops when eligible, persistent button if declined.
 *
 * Gating: Phase 2 will move the unlock behind a Roc/Gym quest. V1 is direct access.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SoulKeyPanel from "@/components/SoulKeyPanel";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 🎨 Class portraits (owner-supplied PNGs Feb 2026 — "of Dragonious" set).
// Big square art shown in place of the emoji on the class-picker cards
// and the confirm modal hero. Emoji `c.icon` remains the fallback when
// no portrait is mapped (locked heroes, future classes, image errors).
const PORTRAIT_BASE = "https://customer-assets-rejwkqb3.emergentagent.net/job_omni-brand-1/artifacts";
const CLASS_PORTRAITS = {
  warrior:   `${PORTRAIT_BASE}/f3dmwxjy_warrior-rpg-orange-v1.webp`,  // 🗡️ The Warrior of Dragonious (Feb 2026 v1)
  mage:      `${PORTRAIT_BASE}/kvhs3cyt_mage-rpg-purple-v1.webp`,     // 🔮 The Mage of Dragonious (Feb 2026 v1)
  archer:    `${PORTRAIT_BASE}/y3ybhs82_archer-rpg-green-v1.webp`,    // 🏹 The Archer of Dragonious (Feb 2026 v1)
  ash_rogue: `${PORTRAIT_BASE}/6htsr4yb_ashrogue-rpg-black-v1.webp`,  // 🌑 The Ash Rogue of Dragonious (Feb 2026 v1)
};

// 🎨 Canonical Hero Class Color Palette (owner-locked Feb 2026)
// Applies to: class cards, confirm modal, hero banner, and Lv 1 / Lv 2 ultimate VFX.
// Each class owns ONE signature color — no overlap, no exceptions.
const CLASS_GRADIENTS = {
  warrior:            "from-red-950 via-[#1a0010] to-red-900/40",
  mage:               "from-purple-950 via-[#10001a] to-purple-900/40",
  archer:             "from-green-950 via-[#001a10] to-green-900/40",
  ash_rogue:          "from-neutral-950 via-black to-neutral-900",
  cassandra_auditor:  "from-amber-900 via-[#1a1000] to-yellow-900/50",
  // 🍳 David Lauer — half kitchen-light white, half sapphire light blue (Feb 2026 lock)
  blue_mage:          "from-sky-200 via-white to-sky-300/60",
  commoner_vanguard:  "from-stone-800 via-[#1a0f08] to-amber-900/30",
  blood_practitioner: "from-rose-950 via-[#1a0008] to-red-900/50",
  glitch_architect:   "from-neutral-100 via-neutral-900 to-black",
  war_librarian:      "from-yellow-700 via-[#1a1400] to-amber-700/40",
  shadow_director:    "from-pink-900 via-[#1a000c] to-fuchsia-900/40",
  lawman_brewer:      "from-slate-800 via-[#0a0f1a] to-slate-700/40",
  celestial_vanguard: "from-purple-950 via-[#0a001a] to-amber-900/30",
  // 🔥👑 PROTAGONIST — Iridescent rainbow + phoenix gold split. The
  // rainbow is the full spectrum of every Phoenix-blooded class color
  // refracted through her unbroken soul.
  mother_phoenix:     "from-amber-500 via-rose-500 to-cyan-400",
};

const CLASS_BORDER = {
  warrior:            "border-red-500",
  mage:               "border-purple-500",
  archer:             "border-green-500",
  ash_rogue:          "border-neutral-700",
  cassandra_auditor:  "border-amber-400",
  blue_mage:          "border-sky-300",
  commoner_vanguard:  "border-stone-500",
  blood_practitioner: "border-rose-500",
  glitch_architect:   "border-neutral-300",
  war_librarian:      "border-yellow-400",
  shadow_director:    "border-pink-400",
  lawman_brewer:      "border-slate-400",
  celestial_vanguard: "border-purple-400",
  mother_phoenix:     "border-amber-300",
};

// Solid accent color per class — used for ultimate ability VFX, button glows,
// HP-bar fills, and skill-card stripes. Used BOTH in Lv 1 ultimates AND
// the upgraded Lv 2 versions (Lv 2 simply brightens / saturates).
const CLASS_ACCENT = {
  warrior:            { lv1: "#dc2626", lv2: "#ef4444", name: "Red" },
  mage:               { lv1: "#9333ea", lv2: "#a855f7", name: "Purple" },
  archer:             { lv1: "#16a34a", lv2: "#22c55e", name: "Green" },
  ash_rogue:          { lv1: "#171717", lv2: "#404040", name: "Dark Black" },
  cassandra_auditor:  { lv1: "#d97706", lv2: "#fbbf24", name: "Golden" },
  // David's "White-Blue" — neutral white base, sapphire highlight
  blue_mage:          { lv1: "#bae6fd", lv2: "#ffffff", name: "White-Blue" },
  commoner_vanguard:  { lv1: "#a8a29e", lv2: "#d6d3d1", name: "Rusty Metal" },
  blood_practitioner: { lv1: "#9f1239", lv2: "#e11d48", name: "Crimson" },
  glitch_architect:   { lv1: "#f5f5f5", lv2: "#0a0a0a", name: "CRT White-Black" },
  war_librarian:      { lv1: "#eab308", lv2: "#fde047", name: "Yellow" },
  shadow_director:    { lv1: "#ec4899", lv2: "#f9a8d4", name: "Pink" },
  lawman_brewer:      { lv1: "#475569", lv2: "#94a3b8", name: "Slate" },
  celestial_vanguard: { lv1: "#7c3aed", lv2: "#fbbf24", name: "Cosmic Purple / Gold" },
  // 🔥👑 PROTAGONIST — the iridescent rainbow + gold of the original fire.
  mother_phoenix:     { lv1: "#fbbf24", lv2: "#fde68a", name: "Iridescent Rainbow / Phoenix Gold" },
};

const STAT_META = {
  patk: { label: "PATK", icon: "🗡️", color: "text-red-300" },
  matk: { label: "MATK", icon: "🔮", color: "text-purple-300" },
  pdef: { label: "PDEF", icon: "🛡️", color: "text-blue-300" },
  mdef: { label: "MDEF", icon: "✨", color: "text-amber-300" },
};

// 📜 Map locked class_id → Codex NPC id (journal_service NPC_CATALOG).
// Heroes without a current Codex entry are intentionally omitted —
// the card will hide the deep-link until their NPC ships.
const CODEX_NPC_FOR_CLASS = {
  blue_mage:          "david",
  cassandra_auditor:  "cassandra",
  war_librarian:      "cari",
  lawman_brewer:      "wesley",
  // Locked-hero foreshadow voices (Feb 2026):
  blood_practitioner: "cassandra",   // Auditor's memo about the Sanguine Practitioner
  glitch_architect:   "wesley",      // Incident report from inside the antique TV
  shadow_director:    "david",       // Kitchen log: "Order Up! (Under Protest)"
  celestial_vanguard: "silver_light",// What the moon-rock remembers
  // 🔥👑 PROTAGONIST — Mother Phoenix writes to the player directly.
  mother_phoenix:     "mother_phoenix",
};


export default function HeroClassPage() {
  const { isAuthed } = useAuth();
  const [state, setState] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const [confirming, setConfirming] = useState(null);  // class_id pending confirm
  const [showRebirth, setShowRebirth] = useState(false);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/hero-class/state`);
      setState(r.data);
      setLoadErr(null);
      // Auto-prompt rebirth if eligible AND user hasn't already declined this level
      if (r.data.rebirth_eligibility?.eligible
          && r.data.pending_rebirth_at_level !== r.data.level) {
        setShowRebirth(true);
      }
    } catch (e) {
      // 🧯 Oct 2026 — don't silently swallow. If this fails the whole page
      // body goes blank and the player can't pick a class or see Soul Keys.
      setLoadErr(e?.response?.data?.detail || e?.message || "Couldn't load hero sheet.");
    }
  };

  useEffect(() => {
    if (isAuthed) refresh();
  }, [isAuthed]);

  const onConfirm = async (classId) => {
    setBusy(true);
    try {
      await axios.post(`${API}/hero-class/select`, { class_id: classId });
      toast.success(`🦸 You are now a ${classId.replace("_", " ")}!`);
      setConfirming(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't lock in class.");
    } finally {
      setBusy(false);
    }
  };

  const onRebirth = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/hero-class/rebirth`);
      toast.success(`🔥 Rebirth #${r.data.rebirth_count} — you rise again!`);
      setShowRebirth(false);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rebirth failed.");
    } finally {
      setBusy(false);
    }
  };

  const onDeclineRebirth = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/hero-class/rebirth/decline`);
      setShowRebirth(false);
      refresh();
    } catch {} finally { setBusy(false); }
  };

  if (!isAuthed) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#10001a] via-black to-[#1a0010] text-amber-100">
        <Header />
        <div className="max-w-3xl mx-auto px-4 py-20 text-center">
          <div className="text-6xl mb-4">🦸</div>
          <h1 className="text-4xl font-black mb-2" style={{ fontFamily: "Anton, sans-serif" }}>
            HERO CLASSES
          </h1>
          <p className="text-neutral-400">Sign in to choose your path.</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#10001a] via-black to-[#1a0010] text-amber-100" data-testid="hero-class-page">
      <Header />

      <main className="max-w-6xl mx-auto px-4 py-6 pb-24">
        <div className="rounded-2xl border border-purple-700/40 bg-gradient-to-br from-[#10001a] via-black/60 to-[#1a0010] p-6 mb-6 relative overflow-hidden">
          <div className="absolute -top-8 -right-8 text-[160px] opacity-10 select-none">🦸</div>
          <div className="relative">
            <div className="text-xs uppercase tracking-[0.4em] text-purple-300/70 mb-1">Kingdom of Dragonious</div>
            <h1 className="text-4xl md:text-5xl font-black text-amber-200" style={{ fontFamily: "Anton, sans-serif" }}>
              🦸 HERO CLASSES
            </h1>
            <p className="text-amber-100/80 text-sm mt-2 max-w-2xl">
              Pick your path. <b>Locked in</b> — no swaps in V1. Each class has unique
              PATK/MATK/PDEF/MDEF affinity, gear-lock rules, and a Phoenix Rebirth loop.
            </p>
          </div>
        </div>

        {!state && !loadErr && (
          <div
            className="rounded-xl border border-purple-900/40 bg-black/40 p-10 text-center text-purple-200/70"
            data-testid="hero-class-loading"
          >
            Loading your hero sheet…
          </div>
        )}

        {loadErr && (
          <div
            className="rounded-xl border border-rose-700/40 bg-rose-950/30 p-5 mb-5 text-sm"
            data-testid="hero-class-load-error"
          >
            <div className="font-bold text-rose-200">⚠️ Hero sheet unavailable</div>
            <div className="text-rose-200/80 mt-1">{loadErr}</div>
            <button
              onClick={refresh}
              className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-rose-500/60 text-rose-100 hover:bg-rose-900/40 text-xs font-bold uppercase tracking-widest"
              data-testid="hero-class-retry"
            >
              Retry
            </button>
          </div>
        )}

        {state?.selected ? (
          <SelectedView
            state={state}
            onOpenRebirth={() => setShowRebirth(true)}
          />
        ) : state ? (
          <ClassPicker
            classes={state?.classes_catalog || []}
            onPick={(id) => setConfirming(id)}
          />
        ) : null}

        {/* ☯️ Oct 2026 — Soul Keys surface on the Hero page. Roc's regression
            note: Soul Keys exist in the backend + Guild Lever drops but had
            no reliable home on the player's own hero sheet. Also exposes
            the Battle Power / Aether & Ash page as a quick link so players
            see their hero number right next to their class. */}
        {state?.selected && (
          <div className="mt-8 grid gap-6 md:grid-cols-[1fr_280px]">
            <SoulKeyPanel />
            <div className="space-y-3">
              <Link
                to="/aether-and-ash"
                data-testid="hero-aa-link"
                className="relative block rounded-2xl border border-amber-800/50 bg-gradient-to-br from-amber-950/40 via-black to-rose-950/30 p-5 overflow-hidden group hover:border-amber-500/70 transition-all"
              >
                <div className="absolute -top-6 -right-6 text-[90px] opacity-10 select-none">⚡</div>
                <div className="relative">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-amber-400/70 mb-1">
                    Hero Battle Power
                  </div>
                  <div className="font-display text-2xl text-amber-200 leading-tight mb-1">
                    Aether &amp; Ash
                  </div>
                  <div className="text-[11px] text-amber-100/60 leading-relaxed">
                    Your 40-rung ladder standing — Aether (Level · Skills) + Ash (gear · HP · tower · mount · pet).
                  </div>
                  <div className="mt-3 inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-widest text-amber-300 group-hover:text-amber-200">
                    Open ladder →
                  </div>
                </div>
              </Link>
              <Link
                to="/hero-codex"
                data-testid="hero-codex-link"
                className="relative block rounded-2xl border border-indigo-800/50 bg-gradient-to-br from-indigo-950/40 via-black to-fuchsia-950/30 p-5 overflow-hidden group hover:border-indigo-500/70 transition-all"
              >
                <div className="absolute -top-6 -right-6 text-[90px] opacity-10 select-none">📖</div>
                <div className="relative">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-indigo-400/70 mb-1">
                    Full Kit · Every Hero
                  </div>
                  <div className="font-display text-2xl text-indigo-200 leading-tight mb-1">
                    Hero Codex
                  </div>
                  <div className="text-[11px] text-indigo-100/60 leading-relaxed">
                    Generator · Secondary · Body · Passive · Super Passive · Ultimate · Super Ultimate · Combo (locked).
                  </div>
                  <div className="mt-3 inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-widest text-indigo-300 group-hover:text-indigo-200">
                    Open codex →
                  </div>
                </div>
              </Link>
            </div>
          </div>
        )}

        {confirming && state?.classes_catalog && (
          <ConfirmModal
            cls={state.classes_catalog.find((c) => c.id === confirming)}
            busy={busy}
            onClose={() => setConfirming(null)}
            onConfirm={() => onConfirm(confirming)}
          />
        )}

        {showRebirth && state?.rebirth_eligibility?.eligible && (
          <RebirthModal
            state={state}
            busy={busy}
            onAccept={onRebirth}
            onDecline={onDeclineRebirth}
          />
        )}
      </main>

      <Footer />
    </div>
  );
}


function ClassPicker({ classes, onPick }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="class-picker">
      {classes.map((c) => {
        const locked = !!c.gated;
        return (
        <button
          key={c.id}
          onClick={() => {
            if (locked) {
              toast(`🔒 ${c.name} is locked`, { description: c.gate_reason || "Coming soon." });
              return;
            }
            onPick(c.id);
          }}
          data-testid={`class-card-${c.id}`}
          aria-disabled={locked}
          className={`group relative rounded-2xl border-2 ${CLASS_BORDER[c.id] || "border-neutral-700"} bg-gradient-to-br ${CLASS_GRADIENTS[c.id] || "from-neutral-900 to-black"} p-6 text-left transition-transform ${locked ? "opacity-60 cursor-not-allowed grayscale-[35%]" : "hover:scale-[1.02]"}`}
        >
          {locked && (
            <div
              className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-black/70 border border-amber-400/60 text-[10px] uppercase tracking-[0.2em] text-amber-300 font-black"
              data-testid={`class-locked-badge-${c.id}`}
            >
              🔒 Locked
            </div>
          )}
          <div className="flex items-start gap-4 mb-3">
            {/* 🎨 Portrait when available, otherwise the giant emoji fallback */}
            {CLASS_PORTRAITS[c.id] ? (
              <img
                src={CLASS_PORTRAITS[c.id]}
                alt={`${c.name} portrait`}
                data-testid={`class-portrait-${c.id}`}
                className="
                  w-24 h-24 object-cover rounded-xl border-2 border-amber-400/40
                  shadow-[0_0_20px_rgba(212,175,55,0.25)]
                  transition-[transform,box-shadow,border-color,filter] duration-500 ease-out
                  group-hover:scale-[1.06]
                  group-hover:border-[#DC2626]/80
                  group-hover:shadow-[0_0_28px_rgba(147,51,234,0.55),0_0_44px_rgba(220,38,38,0.45)]
                  group-hover:saturate-125 group-hover:brightness-110
                  group-active:scale-[1.06]
                  group-active:border-[#DC2626]/80
                  group-active:shadow-[0_0_28px_rgba(147,51,234,0.55),0_0_44px_rgba(220,38,38,0.45)]
                  group-active:saturate-125 group-active:brightness-110
                  flex-shrink-0
                "
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            ) : (
              <div className="text-6xl">{c.icon}</div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300/70">Class</div>
              <h3 className="text-3xl font-black text-amber-200 leading-tight" style={{ fontFamily: "Anton, sans-serif" }}>
                {c.name.toUpperCase()}
              </h3>
              {c.picker_title && c.picker_title !== c.name && (
                <div className="text-[11px] uppercase tracking-widest text-amber-300/80 font-bold mt-0.5" data-testid={`class-pick-title-${c.id}`}>
                  {c.picker_title}
                </div>
              )}
              <p className="text-xs text-amber-100/80 italic mt-1">{c.tagline}</p>
            </div>
          </div>
          <p className="text-sm text-amber-100/85 leading-relaxed font-serif mb-3">
            {c.long_desc}
          </p>
          <div className="grid grid-cols-4 gap-2 mb-3">
            {["patk", "matk", "pdef", "mdef"].map((s) => (
              <div key={s} className="rounded bg-black/40 border border-purple-700/30 p-2 text-center">
                <div className={`text-[9px] uppercase tracking-widest ${STAT_META[s].color}`}>{STAT_META[s].label}</div>
                <div className="text-base font-black text-amber-100">{c.base_stats[s]}</div>
              </div>
            ))}
          </div>
          {/* ⚡ Skills preview — owner-spec'd display: hero pick shows name+title+SKILLS */}
          {Array.isArray(c.skills) && c.skills.length > 0 && (
            <div className="mb-3" data-testid={`class-pick-skills-${c.id}`}>
              <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300/70 mb-1.5">Signature Skills</div>
              <div className="grid grid-cols-1 gap-1.5">
                {c.skills
                  .filter((s) => s.kind !== "trigger" && !["basic_strike", "basic_bolt"].includes(s.id))
                  .map((s) => {
                    const kindBadge =
                      s.kind === "ultimate" ? "text-purple-300 bg-purple-900/30 border-purple-700/50" :
                      s.kind === "heal"     ? "text-emerald-300 bg-emerald-900/30 border-emerald-700/50" :
                      s.kind === "absorb"   ? "text-cyan-300 bg-cyan-900/30 border-cyan-700/50" :
                      s.kind === "leech"    ? "text-rose-300 bg-rose-900/30 border-rose-700/50" :
                                              "text-amber-300 bg-amber-900/30 border-amber-700/50";
                    return (
                      <div key={s.id} className="flex items-start gap-2 rounded bg-black/30 border border-amber-900/40 px-2 py-1.5">
                        <div className="text-lg shrink-0">{s.icon || "✨"}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-xs font-black text-amber-100">{s.name}</span>
                            <span className={`text-[8px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded border ${kindBadge}`}>
                              {s.kind === "ultimate" ? "Ult" : s.kind === "atk_p" ? "Phys" : s.kind === "atk_m" ? "Mag" : s.kind}
                            </span>
                          </div>
                          <div className="text-[10px] text-amber-100/70 line-clamp-2 mt-0.5">{s.description}</div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
          {/* 🤝 Synergies — party-building hints (Feb 2026) */}
          {Array.isArray(c.synergies) && c.synergies.length > 0 && (
            <div className="mb-3" data-testid={`class-pick-synergies-${c.id}`}>
              <div className="text-[10px] uppercase tracking-[0.3em] text-cyan-300/70 mb-1.5">🤝 Party Synergies</div>
              <div className="flex flex-wrap gap-1.5">
                {c.synergies.map((s) => (
                  <span
                    key={`${c.id}-syn-${s.partner}`}
                    title={`${s.title} — ${s.blurb}`}
                    data-testid={`synergy-${c.id}-${s.partner}`}
                    className={`px-2 py-1 rounded-full text-[9px] font-bold uppercase tracking-wider border ${
                      s.future
                        ? "bg-purple-900/30 border-purple-600/50 text-purple-200"
                        : "bg-cyan-900/30 border-cyan-600/50 text-cyan-200"
                    }`}>
                    {s.icon} {s.title}{s.future ? " (planned)" : ""}
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2 text-[11px]">
            <span className="px-2 py-0.5 rounded-full bg-amber-500/20 border border-amber-500/50 text-amber-200">{c.weapon_slot_label}</span>
            <span className="px-2 py-0.5 rounded-full bg-amber-500/20 border border-amber-500/50 text-amber-200">{c.armor_slot_label}</span>
          </div>
          <div className="mt-3 text-[10px] text-neutral-500 italic">{c.lore}</div>
          {locked ? (
            <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
              <div className="text-amber-400/80 text-xs font-black uppercase tracking-widest">
                🔒 {c.gate_reason || "Unlock coming soon"}
              </div>
              {CODEX_NPC_FOR_CLASS[c.id] && (
                <a
                  href={`/codex?npc=${CODEX_NPC_FOR_CLASS[c.id]}`}
                  onClick={async (e) => {
                    e.stopPropagation();
                    // 📜 Idempotently seed the canon foreshadow entry so the
                    // Codex actually has something to show for this hero.
                    // Non-blocking, best-effort — the navigation proceeds
                    // regardless of API outcome.
                    try {
                      await axios.post(`${API}/journals/foreshadow/${c.id}`);
                    } catch (_) { /* silent */ }
                  }}
                  data-testid={`class-codex-link-${c.id}`}
                  className="text-[11px] font-bold text-amber-300 hover:text-amber-200 underline-offset-2 hover:underline whitespace-nowrap"
                >
                  📜 Read in Codex →
                </a>
              )}
            </div>
          ) : (
            <div className="mt-4 text-amber-300 text-sm font-black">→ Choose {c.name}</div>
          )}
        </button>
        );
      })}
    </div>
  );
}


function ConfirmModal({ cls, busy, onClose, onConfirm }) {
  if (!cls) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" data-testid="class-confirm-modal">
      <div className={`relative w-full max-w-md rounded-2xl border-2 ${CLASS_BORDER[cls.id]} bg-gradient-to-br ${CLASS_GRADIENTS[cls.id]} p-6`}>
        <button
          onClick={onClose}
          data-testid="class-confirm-close"
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/60 hover:bg-amber-500/30 flex items-center justify-center text-amber-300 text-lg"
        >×</button>
        <div className="text-center">
          {/* 🎨 Big portrait in the confirm modal — same source of truth */}
          {CLASS_PORTRAITS[cls.id] ? (
            <img
              src={CLASS_PORTRAITS[cls.id]}
              alt={`${cls.name} portrait`}
              data-testid={`class-confirm-portrait-${cls.id}`}
              className="w-40 h-40 mx-auto mb-3 object-cover rounded-2xl border-2 border-amber-400/50 shadow-[0_0_30px_rgba(212,175,55,0.35)]"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            <div className="text-7xl mb-2">{cls.icon}</div>
          )}
          <div className="text-[10px] uppercase tracking-[0.4em] text-amber-300/70">Lock in your path</div>
          <h2 className="text-4xl font-black text-amber-200 mb-3" style={{ fontFamily: "Anton, sans-serif" }}>
            {cls.name.toUpperCase()}
          </h2>
          <p className="text-sm text-amber-100/90 italic font-serif">{cls.tagline}</p>
        </div>
        <div className="mt-5 rounded-lg border border-yellow-700/50 bg-yellow-900/20 p-3 text-[12px] text-amber-100/90">
          ⚠️ <b>This choice is permanent.</b> Class swaps aren't allowed in V1. Pick carefully.
        </div>
        <div className="mt-5 flex gap-3">
          <button
            onClick={onClose}
            disabled={busy}
            className="flex-1 px-5 py-2.5 rounded bg-neutral-800 hover:bg-neutral-700 text-amber-200 font-bold text-sm"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            data-testid="class-confirm-yes"
            className="flex-1 px-5 py-2.5 rounded bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black font-black text-sm"
          >
            {busy ? "Locking..." : "Lock it in 🔥"}
          </button>
        </div>
      </div>
    </div>
  );
}


function SelectedView({ state, onOpenRebirth }) {
  const cls = state.class_meta;
  const stats = state.stats || {};
  const xpPct = Math.min(100, Math.round(((state.xp || 0) / (state.xp_to_next || 1)) * 100));

  // 🧯 Oct 2026 — if class_meta didn't come down with the payload, we
  // used to crash on `cls.id` and the whole page body went blank. Now
  // we render a friendly stub so the player can still re-pick / use
  // Soul Keys instead of staring at an empty screen.
  if (!cls || !cls.id) {
    return (
      <div
        className="rounded-2xl border border-amber-700/40 bg-amber-950/30 p-6 text-amber-100 space-y-3"
        data-testid="hero-class-meta-missing"
      >
        <div className="text-lg font-bold">⚠️ Hero class is set but its definition didn't load.</div>
        <div className="text-sm text-amber-200/80">
          Your account is on <b>{state.class_id || "an unknown class"}</b> at
          Lv <b>{state.level ?? 1}</b> · Rebirth <b>{state.rebirth_count ?? 0}</b>.
          The class catalog entry didn't come back from the server — probably a
          transient 500. Try reloading, or use a Soul Key to swap into a known class.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="hero-selected-view">
      {/* Hero banner */}
      <div className={`rounded-2xl border-2 ${CLASS_BORDER[cls.id] || "border-amber-700/50"} bg-gradient-to-br ${CLASS_GRADIENTS[cls.id] || "from-amber-950/40 via-black to-rose-950/30"} p-6`}>
        <div className="flex items-start gap-5">
          {/* 🎨 Hero banner portrait — once a player locks their class this is the
              big art that greets them every visit. Falls back to emoji if the
              user picked a class without owner-supplied portrait (locked heroes). */}
          {CLASS_PORTRAITS[cls.id] ? (
            <img
              src={CLASS_PORTRAITS[cls.id]}
              alt={`${cls.name} portrait`}
              data-testid={`hero-banner-portrait-${cls.id}`}
              className="w-32 h-32 md:w-40 md:h-40 object-cover rounded-2xl border-2 border-amber-400/50 shadow-[0_0_30px_rgba(212,175,55,0.3)] flex-shrink-0"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            <div className="text-8xl flex-shrink-0">{cls.icon}</div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.4em] text-amber-300/70">Your Path</div>
            <h2 className="text-4xl md:text-5xl font-black text-amber-200" style={{ fontFamily: "Anton, sans-serif" }}>
              {cls.name.toUpperCase()}
            </h2>
            <p className="text-sm text-amber-100/85 italic font-serif mt-1">{cls.tagline}</p>
            <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
              <div className="rounded-full bg-black/50 border border-amber-700/50 px-3 py-1">
                Lvl <b className="text-amber-200">{state.level}</b> / 50
              </div>
              <div className="rounded-full bg-black/50 border border-purple-700/50 px-3 py-1">
                🔥 Rebirth: <b className="text-amber-200">{state.rebirth_count}</b> / {state.max_rebirths}
              </div>
              {state.rebirth_eligibility?.eligible && (
                <button
                  onClick={onOpenRebirth}
                  data-testid="open-rebirth-btn"
                  className="rounded-full bg-amber-500 hover:bg-amber-400 text-black font-black px-3 py-1 animate-pulse"
                >
                  🔥 Rebirth Available!
                </button>
              )}
            </div>
          </div>
        </div>

        {/* XP bar */}
        <div className="mt-5">
          <div className="flex items-center justify-between text-[10px] text-amber-300/70 mb-1">
            <span>XP</span>
            <span><b className="text-amber-200">{state.xp}</b> / {state.xp_to_next}</span>
          </div>
          <div className="h-2 bg-black/60 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-amber-500 to-amber-300 transition-all"
              style={{ width: `${xpPct}%` }}
            />
          </div>
        </div>
      </div>

      {/* Stats panel */}
      <div className="rounded-2xl border border-purple-700/40 bg-gradient-to-br from-[#10001a] to-black/60 p-5">
        <div className="text-[10px] uppercase tracking-[0.3em] text-purple-300/70 mb-1">Combat Stats</div>
        <h3 className="text-2xl font-black text-amber-200 mb-4" style={{ fontFamily: "Anton, sans-serif" }}>
          ⚡ AFFINITY
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {["patk", "matk", "pdef", "mdef"].map((s) => {
            const isPrimary = cls.primary_stat?.toLowerCase() === s;
            const isSecondary = cls.secondary_stat?.toLowerCase() === s;
            return (
              <div
                key={s}
                data-testid={`stat-${s}`}
                className={`rounded-xl p-4 text-center border-2 ${
                  isPrimary ? "border-amber-500 bg-amber-500/10 shadow-[0_0_18px_rgba(245,158,11,0.2)]" :
                  isSecondary ? "border-purple-500 bg-purple-500/10" :
                  "border-neutral-800 bg-black/40"
                }`}
              >
                <div className="text-2xl mb-1">{STAT_META[s].icon}</div>
                <div className={`text-[10px] uppercase tracking-widest ${STAT_META[s].color}`}>
                  {STAT_META[s].label}
                  {isPrimary && " · PRIMARY"}
                  {isSecondary && " · SECONDARY"}
                </div>
                <div className="text-3xl font-black text-amber-100 mt-1">{stats[s] || 0}</div>
              </div>
            );
          })}
        </div>
        {state.rebirth_count > 0 && (
          <div className="mt-4 text-[11px] text-amber-300 italic text-center">
            🔥 +{Math.round(state.rebirth_count * state.rebirth_bonus_pct * 100)}% rebirth bonus applied to primary + secondary stats
          </div>
        )}
      </div>

      {/* CCLL panel */}
      <div className="rounded-2xl border border-yellow-700/40 bg-yellow-900/10 p-5">
        <div className="text-[10px] uppercase tracking-[0.3em] text-yellow-300/80 mb-1">🚫 Cross-Class Loot Lock</div>
        <h3 className="text-lg font-black text-amber-200" style={{ fontFamily: "Anton, sans-serif" }}>
          YOU CAN'T LOOT FOR YOURSELF
        </h3>
        <p className="text-sm text-amber-100/85 mt-2 leading-relaxed font-serif">
          As a <b>{cls.name}</b>, gear locked to <b>{cls.name}</b> can ONLY be acquired by trading
          with another hero. The kingdom forces cooperation by economy. Use the
          🏪 Auction House to find what fits.
        </p>
        <div className="mt-3 flex flex-wrap gap-2 text-xs">
          <span className="text-neutral-400 w-full mb-1">
            Drops/buys gated FROM you ({(state.ccll_locked_classes_meta || state.ccll_locked_classes || []).length} other heroes):
          </span>
          {(state.ccll_locked_classes_meta || []).map((m) => (
            <span
              key={m.id}
              title={m.picker_title || m.name}
              data-testid={`ccll-locked-${m.id}`}
              className="px-2 py-0.5 rounded-full bg-black/40 border border-yellow-700/40 text-yellow-200 font-bold"
            >
              ❌ {m.icon} {m.name}
            </span>
          ))}
          {/* Fallback: if backend didn't send the enriched meta, render IDs */}
          {!state.ccll_locked_classes_meta && (state.ccll_locked_classes || []).map((c) => (
            <span key={c} className="px-2 py-0.5 rounded-full bg-black/40 border border-yellow-700/40 text-yellow-200 font-bold">
              ❌ {c.replace("_", " ")}
            </span>
          ))}
          <span
            data-testid="ccll-current-class"
            className="px-2 py-0.5 rounded-full bg-amber-500/20 border border-amber-500/50 text-amber-200 font-bold"
          >
            ✅ {cls.icon || ""} {cls.name} (only via auction)
          </span>
        </div>
      </div>

      {/* Rebirth history (if any) */}
      {(state.rebirth_history || []).length > 0 && (
        <div className="rounded-2xl border border-purple-700/40 bg-black/40 p-5">
          <div className="text-[10px] uppercase tracking-[0.3em] text-purple-300/70 mb-1">🔥 Rebirth Ledger</div>
          <div className="space-y-2 mt-3">
            {state.rebirth_history.map((h, i) => (
              <div key={i} className="text-xs text-amber-100/80 flex items-center justify-between border-b border-neutral-800 pb-1">
                <span><b className="text-amber-300">#{h.rebirth_number}</b> · from Lv {h.from_level}</span>
                <span className="text-neutral-500">{new Date(h.ts).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


function RebirthModal({ state, busy, onAccept, onDecline }) {
  const elig = state.rebirth_eligibility;
  const cls = state.class_meta;
  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" data-testid="rebirth-modal">
      <div className="relative w-full max-w-md rounded-2xl border-2 border-amber-500 bg-gradient-to-br from-[#1a0500] via-black to-[#1a0010] p-6 shadow-[0_0_40px_rgba(245,158,11,0.4)]">
        <div className="text-center">
          <div className="text-7xl mb-2 animate-pulse">🔥</div>
          <div className="text-[10px] uppercase tracking-[0.4em] text-amber-300/80">Phoenix Death-Rebirth</div>
          <h2 className="text-4xl font-black text-amber-200 mb-2" style={{ fontFamily: "Anton, sans-serif" }}>
            REBIRTH #{elig.rebirth_number}
          </h2>
          <p className="text-sm text-amber-100/90 italic font-serif">
            "The Phoenix dies that the Phoenix may rise stronger. Your ash
            remembers every fight."
          </p>
        </div>
        <div className="mt-5 rounded-lg border border-amber-700/40 bg-black/40 p-4 text-sm text-amber-100/90">
          <div className="text-[11px] uppercase tracking-widest text-purple-300/70 mb-2">What happens</div>
          <ul className="space-y-1.5">
            <li>• Your level resets from <b className="text-amber-200">{elig.from_level}</b> → <b className="text-amber-200">1</b></li>
            <li>• You KEEP your class, gear, recipes, and inventory</li>
            <li>• +{Math.round(elig.bonus_pct * 100)}% permanent bonus to {cls.primary_stat} & {cls.secondary_stat}</li>
            <li>• This bonus stacks with previous rebirths ({state.rebirth_count} so far)</li>
          </ul>
        </div>
        <div className="mt-5 flex gap-3">
          <button
            onClick={onDecline}
            disabled={busy}
            data-testid="rebirth-decline-btn"
            className="flex-1 px-5 py-2.5 rounded bg-neutral-800 hover:bg-neutral-700 text-amber-200 font-bold text-sm"
          >
            Not Yet
          </button>
          <button
            onClick={onAccept}
            disabled={busy}
            data-testid="rebirth-accept-btn"
            className="flex-1 px-5 py-2.5 rounded bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black font-black text-sm"
          >
            {busy ? "🔥 Burning..." : "🔥 Rise Again"}
          </button>
        </div>
        <div className="text-[10px] text-neutral-500 italic text-center mt-3">
          Decline once? The Rebirth button stays available on your hero page.
        </div>
      </div>
    </div>
  );
}
